"use client"

/**
 * La cesta del mercado, guardada en el navegador.
 *
 * Solo guarda que formatos y cuantos, y de que vendedor: nombres, fotos y
 * precios se piden al abrirla, para no enseñar nunca el precio de otro dia.
 *
 * UNA CESTA ES DE UN SOLO VENDEDOR. Cada vendedor cobra en su propia cuenta y
 * entrega por su cuenta, asi que un pago no puede mezclar a dos. Añadir algo de
 * otro vendedor obliga a decidir: vaciar la cesta o no añadirlo.
 */

const CLAVE = "zindra-mercado-cesta"
const AVISO = "zindra-mercado-cesta-cambia"

export interface CestaGuardada {
  vendedorId: string | null
  vendedorNombre: string | null
  lineas: { varianteId: string; cantidad: number }[]
}

const VACIA: CestaGuardada = { vendedorId: null, vendedorNombre: null, lineas: [] }

export function leerCesta(): CestaGuardada {
  if (typeof window === "undefined") return VACIA
  try {
    const cruda = JSON.parse(window.localStorage.getItem(CLAVE) || "null")
    if (!cruda || !Array.isArray(cruda.lineas)) return VACIA
    return {
      vendedorId: typeof cruda.vendedorId === "string" ? cruda.vendedorId : null,
      vendedorNombre: typeof cruda.vendedorNombre === "string" ? cruda.vendedorNombre : null,
      lineas: cruda.lineas
        .filter((l: any) => typeof l?.varianteId === "string" && Number(l?.cantidad) > 0)
        .map((l: any) => ({ varianteId: l.varianteId, cantidad: Math.min(50, Math.floor(Number(l.cantidad))) })),
    }
  } catch {
    return VACIA
  }
}

export function guardarCesta(cesta: CestaGuardada): void {
  try {
    if (cesta.lineas.length) window.localStorage.setItem(CLAVE, JSON.stringify(cesta))
    else window.localStorage.removeItem(CLAVE)
  } catch {
    // Navegacion privada o almacenamiento lleno: la cesta dura lo que la pagina.
  }
  window.dispatchEvent(new Event(AVISO))
}

export function vaciarCesta(): void {
  guardarCesta(VACIA)
}

export function unidadesEnCesta(cesta: CestaGuardada): number {
  return cesta.lineas.reduce((suma, l) => suma + l.cantidad, 0)
}

export type ResultadoDeAnadir = "anadido" | "otro_vendedor"

/**
 * Añade a la cesta. Si ya hay cosas de otro vendedor no hace nada y lo dice,
 * salvo que se pida `vaciarAntes`.
 */
export function anadirALaCesta(
  vendedor: { id: string; nombre: string },
  varianteId: string,
  cantidad: number,
  vaciarAntes = false,
): ResultadoDeAnadir {
  let cesta = leerCesta()
  if (cesta.lineas.length && cesta.vendedorId && cesta.vendedorId !== vendedor.id) {
    if (!vaciarAntes) return "otro_vendedor"
    cesta = VACIA
  }
  const lineas = [...cesta.lineas]
  const existente = lineas.find((l) => l.varianteId === varianteId)
  if (existente) existente.cantidad = Math.min(50, existente.cantidad + cantidad)
  else lineas.push({ varianteId, cantidad: Math.min(50, Math.max(1, Math.floor(cantidad))) })
  guardarCesta({ vendedorId: vendedor.id, vendedorNombre: vendedor.nombre, lineas })
  return "anadido"
}

/** Avisa cada vez que la cesta cambia, en esta pestaña o en otra. Devuelve como dejar de escuchar. */
export function alCambiarLaCesta(escucha: () => void): () => void {
  window.addEventListener(AVISO, escucha)
  window.addEventListener("storage", escucha)
  return () => {
    window.removeEventListener(AVISO, escucha)
    window.removeEventListener("storage", escucha)
  }
}
